/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import {
  Box,
  Grid,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CustomTextField } from '../../index';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ContainedButton } from '../../index';
import { useState } from 'react';

export const CivilServiceTab = (props) => {
  const handleChange = (index, field, value) => {
    props.setEmpCivService((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAdd = () => {
    props.setEmpCivService((prev) => [
      {
        empid: props.empid,
        servicetype: '',
        rating: '',
        dateofexam: null,
        licensenumber: '',
        dateofrelease: null,
      },
      ...prev,
    ]);
  };

  const handleDelete = (index) => {
    props.setEmpCivService((prev) => prev.filter((_, i) => i !== index));
  };

  const boxStyle = {
    mx: 0,
    my: 1,
    px: 3,
    py: 2,
  };

  const [startTouched, setStartTouched] = useState(false);

  return (
    <Box sx={{ ...boxStyle, maxHeight: '400px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {!props.viewOnly && (
          <ContainedButton
            startIcon={<Add />}
            onClick={handleAdd}
            variant="contained"
            btnTitle={'Add Civil Service'}
            style={{ width: 'auto' }}
          />
        )}
      </Box>
      {props.empCivService.map((civ, index) => (
        <Box
          key={index}
          sx={{
            bgcolor: 'rgb(26, 32, 44, 0.6)',
            display: 'flex',
            justifyContent: 'start',
            p: 2,
            mt: 2,
            borderRadius: '5px',
            position: 'relative',
          }}
        >
          {!props.viewOnly && (
            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(index)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                mr: 1,
              }}
            >
              <Close />
            </IconButton>
          )}
          <Grid container columnSpacing={2} sx={{ mt: 3 }}>
            <Grid item xs={6}>
              <CustomTextField
                error={!civ.servicetype}
                errorMsg="Please enter a service type"
                label="Service Type"
                value={civ.servicetype || ''}
                onChange={(e) =>
                  handleChange(index, 'servicetype', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a rating"
                label="Rating"
                value={civ.rating || ''}
                onChange={(e) => handleChange(index, 'rating', e.target.value)}
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Exam"
                  value={civ.dateofexam ? new Date(civ.dateofexam) : null}
                  maxDate={
                    civ.dateofrelease ? new Date(civ.dateofrelease) : undefined
                  }
                  onChange={(newValue) =>
                    handleChange(index, 'dateofexam', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  renderInput={(params) => {
                    const showError = startTouched && !civ.dateofexam;
                    return (
                      <TextField
                        onFocus={() => setStartTouched(true)}
                        required
                        margin="normal"
                        fullWidth
                        {...params}
                        error={showError}
                        helperText={showError ? 'Date of Exam is required' : ''}
                        inputProps={{
                          ...params.inputProps,
                          style: {
                            cursor: props.viewOnly ? 'default' : undefined,
                          },
                        }}
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!civ.licensenumber}
                errorMsg="Please enter a license number"
                label="License Number"
                value={civ.licensenumber || ''}
                onChange={(e) =>
                  handleChange(index, 'licensenumber', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Release"
                  value={civ.dateofrelease ? new Date(civ.dateofrelease) : null}
                  minDate={
                    civ.dateofexam ? new Date(civ.dateofexam) : undefined
                  }
                  onChange={(newValue) =>
                    handleChange(index, 'dateofrelease', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  renderInput={(params) => (
                    <TextField
                      margin="normal"
                      fullWidth
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          cursor: props.viewOnly ? 'default' : undefined,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
